import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {getUntriedCount} from 'src/action-creators/crm';
import {selectUntriedCount} from 'src/selectors/crm';

/**
 * Custom hook to manage untried count polling
 * Only starts polling when candidates are added and continues until untried count reaches zero
 */
export const useUntriedCountPolling = () => {
  const dispatch = useDispatch();
  const untriedCountData = useSelector(selectUntriedCount);
  const untriedCount = untriedCountData?.untriedCount ?? 0;
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const isPollingRef = React.useRef(false);

  const startPolling = React.useCallback(() => {
    if (isPollingRef.current) {
      return; // Already polling
    }

    isPollingRef.current = true;
    intervalRef.current = setInterval(() => {
      dispatch(getUntriedCount());
    }, 2000);
  }, [dispatch]);

  const stopPolling = React.useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  // Stop polling when untried count reaches zero
  React.useEffect(() => {
    if (untriedCount === 0 && isPollingRef.current) {
      stopPolling();
    }
  }, [untriedCount, stopPolling]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  // Listen for candidate addition events
  React.useEffect(() => {
    const handleCandidateAdded = () => {
      startPolling();
    };

    // Listen for custom event when candidates are added
    window.addEventListener('candidateAdded', handleCandidateAdded);

    return () => {
      window.removeEventListener('candidateAdded', handleCandidateAdded);
    };
  }, [startPolling]);

  return {
    startPolling,
    stopPolling,
    isPolling: isPollingRef.current,
    untriedCount,
  };
};
